import { ChevronDown } from '@shared/icons';
import { twMerge } from '@shared/themes';
import { useTranslations } from 'next-intl';
import { type ComponentProps, useId, useState } from 'react';
import { TotalWinnings } from '../../../../lotteries/TotalWinnings/TotalWinnings';
import type { Line, LotteryName } from '../../../../lottery-purchases/types';
import { hasMatchedNumbers } from '../../../liveDraw/getMatchedNumbers';
import type { DrawnNumbers } from '../../../liveDraw/getRevealedNumbers';
import { getYourNumbersLineMatching } from '../getYourNumbersLineMatching';
import { YourNumbersCard } from '../YourNumbersCard/YourNumbersCard';
import { YourNumbersLine, YourNumbersLinePill, type YourNumbersMatching } from '../YourNumbersLine/YourNumbersLine';

const MAX_COLLAPSED_STACK_CARDS = 2;
const COLLAPSED_STACK_CARDS = [
  { insetClass: 'px-2.5', zClass: 'z-20' },
  { insetClass: 'px-6', zClass: 'z-10' },
] as const;

type YourNumbersDrawnLine = {
  matching?: YourNumbersMatching;
  /** Prize for this line, in cents. */
  prize?: number;
};

type YourNumbersPodProps = ComponentProps<'div'> & {
  lines: Line[];
  lotteryName: LotteryName;
  footerMessageIndex: number;
  showTitle?: boolean;
  defaultExpanded?: boolean;
  /**
   * Revealed prefix from `useLiveDraw().revealedNumbers`. Matching is derived with
   * {@link getMatchedNumbers} so Your Numbers and the live-draw balls share the same prefix.
   */
  revealedNumbers?: DrawnNumbers;
  /**
   * Per-line prizes and optional explicit matching, keyed by `lineNumber`. When `revealedNumbers`
   * is also set, that prefix wins and `matching` on each line is ignored. Prizes are held back
   * until `isDrawComplete` so they cannot appear while the live-draw animation is still playing.
   */
  drawnLines?: Record<number, YourNumbersDrawnLine>;
  /**
   * Set once the draw animation has finished. This is what swaps the footer from the live-draw "Match!" message to the
   * final total, so it must be set even for a losing ticket (where `totalWinnings` is `0`).
   */
  isDrawComplete?: boolean;
  /** Prize tiers are still pending after the draw animation. Shows the calculating footer from WEBD-923. */
  isCalculating?: boolean;
  /** Total winnings across all lines, in cents. Only rendered once `isDrawComplete` is set. */
  totalWinnings?: number;
  /** Renders the total / calculating footer once the draw is complete. Off where only per-line prizes are wanted. */
  showTotalWinnings?: boolean;
};

function getCollapsedStackCardCount(lineCount: number) {
  return Math.min(Math.max(lineCount - 1, 0), MAX_COLLAPSED_STACK_CARDS);
}

function hasHighlightedMatch(matchings: Array<YourNumbersMatching | undefined>) {
  return matchings.some(matching => Boolean(matching?.isHighlighted));
}

function YourNumbersPod({
  lines,
  lotteryName,
  footerMessageIndex,
  showTitle = false,
  defaultExpanded = false,
  revealedNumbers,
  drawnLines,
  isDrawComplete = false,
  isCalculating = false,
  totalWinnings,
  showTotalWinnings = true,
  className,
  ...props
}: YourNumbersPodProps) {
  const t = useTranslations('lotteries.results.your_numbers');
  const footerMessages = (t.has('footer.messages') ? t.raw('footer.messages') : []) as string[];
  const footerMessage = footerMessages[footerMessageIndex % footerMessages.length] ?? '';
  const [isExpandedState, setIsExpandedState] = useState(defaultExpanded);
  const canToggle = lines.length > 1;
  const isExpanded = canToggle ? isExpandedState : true;
  const cardId = useId();
  const stackCardCount = isExpanded ? 0 : getCollapsedStackCardCount(lines.length);
  const visibleLines = isExpanded ? lines : lines.slice(0, 1);
  const lineMatchings = lines.reduce<Record<number, YourNumbersMatching | undefined>>((matchings, line) => {
    const drawnLine = drawnLines?.[line.lineNumber];
    matchings[line.lineNumber] = getYourNumbersLineMatching({
      line,
      revealedNumbers,
      matching: drawnLine?.matching,
      prize: drawnLine?.prize,
      isDrawComplete,
    });
    return matchings;
  }, {});
  const matchings = Object.values(lineMatchings);
  const hasMatchFooter = !isDrawComplete && !isCalculating && matchings.some(hasMatchedNumbers);
  const hasPositiveWinnings = (totalWinnings ?? 0) > 0;
  const isTotalFooter = isDrawComplete || isCalculating;
  const hasFooter = !isTotalFooter || showTotalWinnings;
  const isPrizeOnly = isDrawComplete && !isCalculating && hasPositiveWinnings && !hasHighlightedMatch(matchings);

  return (
    <div className={twMerge('flex w-full flex-col gap-4', className)} data-testid="your-numbers-pod" {...props}>
      <div className={twMerge('flex w-full items-center gap-2', showTitle ? 'justify-between' : 'justify-end')} data-testid="your-numbers-pod-header">
        {showTitle ? (
          <h2 className="heading-5 text-foreground" data-testid="your-numbers-pod-title">
            {t('title')}
          </h2>
        ) : null}
        <button
          type="button"
          onClick={() => setIsExpandedState(prev => !prev)}
          disabled={!canToggle}
          aria-expanded={isExpanded}
          aria-controls={cardId}
          className="flex h-5 cursor-pointer items-center justify-end gap-1.5 disabled:cursor-default"
          data-testid="your-numbers-pod-expand-control"
        >
          <span className="paragraph-explanatory-strong text-foreground whitespace-nowrap" data-testid="your-numbers-pod-line-count">
            {t('lines', { count: lines.length })}
          </span>
          {canToggle ? (
            <ChevronDown
              size="tiny"
              aria-hidden
              className={twMerge('shrink-0 text-foreground transition-transform duration-200', isExpanded && 'rotate-180')}
              data-testid="your-numbers-pod-chevron"
            />
          ) : null}
        </button>
      </div>

      <YourNumbersCard id={cardId} role="region" aria-label={t('title')} className={twMerge('overflow-clip', hasFooter && 'pb-4')} data-testid="your-numbers-pod-card">
        <div className={`bg-${lotteryName.toLowerCase()} flex w-full flex-col items-center p-4`} data-testid="your-numbers-pod-numbers">
          <div className={twMerge('flex w-full max-w-[480px] flex-col', isExpanded ? 'gap-3' : 'relative isolate')}>
            {visibleLines.map(line => {
              const drawnLine = drawnLines?.[line.lineNumber];

              return (
                <YourNumbersLine
                  key={line.lineNumber}
                  className={stackCardCount > 0 ? 'relative z-30 -mb-11' : undefined}
                  lotteryName={lotteryName}
                  result={{ numbers: line.numbers.map(Number), extraNumbers: line.extraNumbers?.map(Number) }}
                  matching={lineMatchings[line.lineNumber]}
                  prize={isDrawComplete && !isCalculating ? drawnLine?.prize : undefined}
                  data-testid={`your-numbers-pod-line-${line.lineNumber}`}
                />
              );
            })}
            {COLLAPSED_STACK_CARDS.slice(0, stackCardCount).map((card, index) => {
              const isLastStackCard = index === stackCardCount - 1;

              return (
                <div
                  key={card.insetClass}
                  className={twMerge('relative w-full', card.insetClass, card.zClass, !isLastStackCard && '-mb-11')}
                  data-testid={`your-numbers-pod-stack-card-${index + 1}`}
                  aria-hidden
                >
                  <YourNumbersLinePill className="h-14" />
                </div>
              );
            })}
          </div>
        </div>
        {/* The footer swaps between the luck message, "Match!" and the final total as the draw plays out, so the change
            needs announcing rather than being left as a silent visual update. */}
        {hasFooter ? (
          <div className={twMerge('flex w-full items-center px-4', isTotalFooter ? 'justify-between' : 'justify-center')} role="status" data-testid="your-numbers-pod-footer">
            {isTotalFooter ? (
              <TotalWinnings calculating={isCalculating} totalWinnings={totalWinnings} titleKey={isPrizeOnly ? 'prize' : 'title'} className="w-full border-0 py-0" />
            ) : (
              <p className="paragraph-l-strong text-foreground text-center" data-testid="your-numbers-pod-message">
                {hasMatchFooter ? t('match') : footerMessage}
              </p>
            )}
          </div>
        ) : null}
      </YourNumbersCard>
    </div>
  );
}

export { YourNumbersPod, type YourNumbersDrawnLine, type YourNumbersPodProps };
